import { runtimeConfig } from '@plone/volto/runtime_config';
import type { SiteConfig, SiteId } from '../types';
import standards from './standards';
import directives from './directives';
import nnsa from './nnsa';

/**
 * The operator, for every slot where a reader expects to be told WHO PUBLISHES
 * this -- `og:site_name`, the title's brand suffix, a sender name. Kept apart
 * from `SiteConfig.seoName` on purpose: `seoName` names the CORPUS and contains
 * an agency name, and putting an agency name in a publisher slot is the
 * false-affiliation failure BL-0082 marked REQUIRED to avoid.
 *
 * The longer legal form, `Doxcelerate Corporation`, stays in the JSON-LD
 * `publisher` Organization and in the footer disclaimer.
 */
export const PUBLISHER_NAME = 'Doxcelerate';

/**
 * The publisher's full legal name, for the slots where the claim is a legal one
 * about who stands behind this archive: the affiliation notice above the
 * masthead, and the JSON-LD `publisher` Organization. `PUBLISHER_NAME` above is
 * the short brand form, for titles and og:site_name.
 *
 * The same literal still appears in prose (the privacy notice, the footer
 * copyright, each site's long-form `disclaimer`). Those are sentences, not
 * slots, and are deliberately left alone.
 */
export const PUBLISHER_LEGAL_NAME = 'Doxcelerate Corporation';

export const SITE_CONFIGS: Record<SiteId, SiteConfig> = {
  standards,
  directives,
  nnsa,
};

/** ordered for prefix matching; first match wins */
const ALL: SiteConfig[] = [standards, directives, nnsa];

/**
 * Which site a path belongs to. The three archives live under distinct first
 * path segments (`/standards`, `/directives`, `/nnsa`), so the segment is an
 * unambiguous key. Handles Volto's flattened `@id` (which may still carry the
 * backend origin) as well as a plain router pathname.
 */
export function siteIdForPath(path?: string | null): SiteId | null {
  if (!path) return null;
  // strip protocol/host and any `/++api++` marker, keep the path
  const clean = String(path)
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/\/\+\+api\+\+/g, '');
  const seg = clean.split('/').filter(Boolean)[0];
  const hit = ALL.find((s) => s.pathPrefix === seg);
  return hit ? hit.id : null;
}

/**
 * Site id bound to THIS running instance, from runtime env (works in one
 * shared build: Volto serializes runtime RAZZLE_* vars into window.env, so
 * the value is identical on the server and the client — no @id sniffing).
 * RAZZLE_SITE_ID wins; otherwise the trailing segment of the instance's
 * API path (RAZZLE_INTERNAL_API_PATH=.../standards|directives|nnsa).
 */
export function siteIdFromEnv(): SiteId | null {
  const explicit = runtimeConfig.RAZZLE_SITE_ID;
  if (explicit && explicit in SITE_CONFIGS) return explicit as SiteId;
  const api =
    runtimeConfig.RAZZLE_INTERNAL_API_PATH || runtimeConfig.RAZZLE_API_PATH;
  if (api) {
    const seg = String(api).replace(/\/+$/, "").split("/").pop();
    if (seg && seg in SITE_CONFIGS) return seg as SiteId;
  }
  return null;
}

/**
 * Resolve the active SiteConfig. Accepts a content object (uses its `@id`),
 * a string path, or nothing. Falls back to `standards` so a component never
 * renders without a brand.
 */
export function getSiteConfig(
  source?: { '@id'?: string } | string | null,
): SiteConfig {
  const path =
    typeof source === 'string' ? source : source && source['@id'];
  const id = siteIdFromEnv() || siteIdForPath(path);
  return id ? SITE_CONFIGS[id] : standards;
}

export type { SiteConfig, SiteId };
